import React, { useContext, useEffect, useState, useRef } from 'react';
import DataContext from '../../stores/DataContextProvider';
import { useNavigate } from 'react-router-dom';
import backendIP from '../../utils/serverData';
import Popup from '../popup/Popup';
import './TestScreen11.css';
import Goal from '../controllers/Goal';

const TestScreen11 = () => {
    const { g, sk, instruction, selectedOptions, folderPath } = useContext(DataContext);
    const [displayScript, setDisplayScript] = useState('');
    const [score, setScore] = useState(0);
    const [totalAudioPlayed, setTotalAudioPlayed] = useState(0);
    const [showPopup, setShowPopup] = useState(false);
    const [playedFiles, setPlayedFiles] = useState([]);
    const [popupError, setPopupError] = useState(null);

    const audioRef = useRef(new Audio());
    const abortControllerRef = useRef(new AbortController());
    const navigate = useNavigate();

    const [isTestRunning, setIsTestRunning] = useState(false);
    const [numberOfStimuli, setNumberOfStimuli] = useState(0);
    const [responseWindow, setResponseWindow] = useState(3000); // ms
    const [duration, setDuration] = useState(0); // seconds
    const [currentStimulusIndex, setCurrentStimulusIndex] = useState(0);
    const [isWithinResponseWindow, setIsWithinResponseWindow] = useState(false);

    const timeoutRef = useRef(null);           // general purpose timeout
    const testTimerRef = useRef(null);         // overall test watchdog
    const startTimeRef = useRef(null);
    const respTimeoutRef = useRef(null);       // response-window timer (dedicated)

    const finalGapSeconds = 10; // final gap after last stimulus (seconds)
    const extraGapSecondsDefault = 3; // preferred extra beyond response window (you asked ~3s)

    // Robust parser for Response Window values
    const parseResponseWindowToMs = (raw) => {
        if (raw == null) return 3000;
        if (typeof raw === 'object') raw = raw.value ?? raw.label ?? raw;
        const s = String(raw).trim().toLowerCase();
        const m = s.match(/-?\d+(\.\d+)?/);
        if (!m) return 3000;
        const num = Number(m[0]);
        if (Number.isNaN(num)) return 3000;
        // if <= 10 treat as seconds else treat as ms
        return Math.abs(num) <= 10 ? Math.round(num * 1000) : Math.round(num);
    };

    useEffect(() => {
        if (selectedOptions && Object.keys(selectedOptions).length > 0) {
            const durationInMinutes =
                parseInt(selectedOptions['Time Duration']?.label ??
                           selectedOptions['Time Duration']?.value ??
                           selectedOptions['Time Duration'], 10) || 0;

            const numStim =
                parseInt(selectedOptions['Number of Stimuli']?.label ??
                          selectedOptions['Number of Stimuli']?.value ??
                          selectedOptions['Number of Stimuli'], 10) || 0;

            const rawResp = selectedOptions['Response Window'] ??
                            selectedOptions['Response Window']?.label ??
                            selectedOptions['Response Window']?.value;
            const respMs = parseResponseWindowToMs(rawResp);

            const totalDurationSec = durationInMinutes * 60;

            if (!isNaN(numStim) && numStim > 0) {
                setNumberOfStimuli(numStim);
                setResponseWindow(respMs);
                setIsTestRunning(true);
                setPlayedFiles([]);
                setDuration(totalDurationSec);
                setScore(0);
                setTotalAudioPlayed(0);
                setPopupError(null);
                console.log(`[Test] init: durationMin=${durationInMinutes}, stimuli=${numStim}, respMs=${respMs}`);
            } else {
                console.error('Invalid Number of Stimuli', numStim);
            }
        }
    }, [selectedOptions]);

    useEffect(() => {
        if (folderPath && numberOfStimuli > 0 && isTestRunning) {
            console.log('[Test] fetching files from', folderPath);
            fetchSelectedAudio();
        }

        return () => {
            // full cleanup on unmount or dependency change
            try { audioRef.current.pause(); } catch (e) {}
            audioRef.current.src = '';
            try { abortControllerRef.current.abort(); } catch (e) {}
            clearTimeout(timeoutRef.current);
            clearTimeout(testTimerRef.current);
            if (respTimeoutRef.current) { clearTimeout(respTimeoutRef.current); respTimeoutRef.current = null; }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [folderPath, numberOfStimuli, isTestRunning]);

    // utility: repeat files to reach desired count and shuffle
    const makeExactCount = (files, desired) => {
        const out = [];
        if (!files || files.length === 0) return out;
        for (let i = 0; i < desired; i++) out.push(files[i % files.length]);
        for (let i = out.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [out[i], out[j]] = [out[j], out[i]];
        }
        return out;
    };

    const requestFileList = async (folder) => {
        const resp = await fetch(`${backendIP}/audio/listfiles`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ folderPath: folder }),
            signal: abortControllerRef.current.signal,
        });
        if (!resp.ok) {
            const txt = await resp.text().catch(()=>'');
            throw new Error(`listfiles failed ${resp.status} ${txt}`);
        }
        return await resp.json();
    };

    const fetchBlobAndMeasure = async (filenameWithPath) => {
        const resp = await fetch(`${backendIP}/audio/getaudio`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ filenameWithPath }),
            signal: abortControllerRef.current.signal,
        });
        if (!resp.ok) {
            const txt = await resp.text().catch(()=>'');
            throw new Error(`getaudio failed ${resp.status} ${txt}`);
        }
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const temp = new Audio();
        temp.src = url;
        const dur = await new Promise((resolve, reject) => {
            const onMeta = () => { cleanup(); resolve(Math.max(0, temp.duration || 0)); };
            const onErr = () => { cleanup(); reject(new Error('loadedmetadata failed')); };
            const cleanup = () => { temp.removeEventListener('loadedmetadata', onMeta); temp.removeEventListener('error', onErr); };
            temp.addEventListener('loadedmetadata', onMeta);
            temp.addEventListener('error', onErr);
        });
        return { url, duration: dur };
    };

    // Attempt to make random gaps >= minGap (seconds). Distribute remaining extra randomly.
    // If totalAvailable < requiredMin, returns null.
    const randomPartitionMin = (totalAvailable, partsCount, minGap) => {
        if (partsCount <= 0) return [];
        const minTotal = minGap * partsCount;
        if (totalAvailable < minTotal) return null;
        const extra = totalAvailable - minTotal;
        const weights = Array.from({ length: partsCount }, () => Math.random());
        const s = weights.reduce((a,b)=>a+b, 0) || 1;
        const parts = weights.map(w => minGap + (w / s) * extra);
        return parts.map(x => Math.round(x * 10) / 10);
    };

    // This produces gaps as you described: variable values like [9,11,17,...].
    // Strategy:
    // 1) Try minGap = responseSec + extraGapSeconds (preferred).
    // 2) If not possible, try minGap = responseSec (no extra).
    // 3) If still not possible, fall back to equal gaps (so we still play all stimuli).
    const generateGaps = (totalAvailable, gapsCount, responseWindowMs, extraGapSeconds) => {
        const responseSec = responseWindowMs / 1000;
        const preferredMin = responseSec + extraGapSeconds;
        const fallbackMin = responseSec;

        // Try preferred
        let res = randomPartitionMin(totalAvailable, gapsCount, preferredMin);
        if (res) return { gaps: res, usedMin: preferredMin, mode: 'preferred' };

        // Try fallback (only response window as min)
        res = randomPartitionMin(totalAvailable, gapsCount, fallbackMin);
        if (res) return { gaps: res, usedMin: fallbackMin, mode: 'fallback-response-only' };

        // Can't even satisfy response window per gap — fall back to equal gaps (may be < response window).
        if (gapsCount <= 0) return { gaps: [], usedMin: 0, mode: 'none' };
        const equal = Array.from({ length: gapsCount }, () => Math.round((totalAvailable / gapsCount) * 10) / 10);
        return { gaps: equal, usedMin: totalAvailable / gapsCount, mode: 'equal-reduced' };
    };

    const fetchSelectedAudio = async () => {
        try {
            abortControllerRef.current = new AbortController();
            setPopupError(null);

            const audioData = await requestFileList(folderPath);
            if (!Array.isArray(audioData) || audioData.length === 0) {
                throw new Error('No audio files returned from server.');
            }

            const wavFiles = audioData
                .filter(f => typeof f === 'string' && f.trim().toLowerCase().endsWith('.wav'))
                .map(f => f.trim());
            if (wavFiles.length === 0) throw new Error('No .wav files in directory.');

            // pick exact number of stimuli (repeat if needed)
            const selected = makeExactCount(wavFiles.sort(() => Math.random() - 0.5), numberOfStimuli);
            setPlayedFiles(selected);
            console.log('[Test] selected sequence:', selected);

            // preload unique files and measure durations
            const preloadMap = {};
            const uniques = Array.from(new Set(selected));
            for (const f of uniques) {
                if (abortControllerRef.current.signal.aborted) throw new Error('Aborted');
                const path = `${folderPath}/${f}`;
                const info = await fetchBlobAndMeasure(path);
                preloadMap[f] = info;
            }

            // compute sum of durations according to the sequence (count duplicates)
            let sumAudioDurations = 0;
            for (const f of selected) sumAudioDurations += (preloadMap[f]?.duration ?? 0);
            console.log('[Test] sum audio durations (s):', sumAudioDurations);

            // compute totalAvailableForGaps (exclude final gap)
            const n = selected.length;
            let totalAvailableForGaps = duration - sumAudioDurations - finalGapSeconds;
            if (totalAvailableForGaps < 0) {
                // If negative, set to 0: means audio durations + final gap exceed total duration.
                // We'll still attempt to play back-to-back (gaps 0) and open response windows.
                console.warn('[Test] totalAvailableForGaps < 0 — audio durations + final gap exceed total duration. Gaps will be zero.');
                totalAvailableForGaps = 0;
            }

            const gapsCount = Math.max(0, n - 1);
            const { gaps, usedMin, mode } = generateGaps(totalAvailableForGaps, gapsCount, responseWindow, extraGapSecondsDefault);

            console.log('[Test] gaps (s):', gaps, 'mode:', mode, 'usedMin:', usedMin, 'finalGap:', finalGapSeconds);

            // If mode is 'equal-reduced' then we could not keep responseWindow as min between stimuli.
            // Notify user so they are aware that it's not possible to satisfy the exact preferred spacing.
            if (mode === 'equal-reduced') {
                setPopupError('Selected total duration too short to maintain response-window-based minimum spacing; gaps reduced so all stimuli still play.');
            }

            // Start playback sequence
            startTimeRef.current = Date.now();
            if (testTimerRef.current) clearTimeout(testTimerRef.current);
            testTimerRef.current = setTimeout(() => {
                console.log('[Test watchdog] duration exceeded - aborting');
                try { abortControllerRef.current.abort(); } catch (e) {}
            }, (duration + 5) * 1000); // small margin

            for (let i = 0; i < selected.length; i++) {
                if (abortControllerRef.current.signal.aborted) {
                    console.log('[Test] aborted during playback at index', i);
                    break;
                }

                const file = selected[i];
                setCurrentStimulusIndex(i);
                setDisplayScript(file.replace('.wav', ''));
                console.log(`[Test] playing ${i+1}/${selected.length}: ${file}`);

                // Ensure preload info present
                const info = preloadMap[file];
                if (!info) throw new Error('Missing preload info for ' + file);

                // START response window at audio START and schedule closure after responseWindow ms
                if (respTimeoutRef.current) {
                    clearTimeout(respTimeoutRef.current);
                    respTimeoutRef.current = null;
                }
                setIsWithinResponseWindow(true);
                respTimeoutRef.current = setTimeout(() => {
                    respTimeoutRef.current = null;
                    setIsWithinResponseWindow(false);
                    console.log('[Test] response window closed after configured ms');
                }, responseWindow);

                // Play audio and await its ended event (we DO NOT clear respTimeoutRef on ended)
                await new Promise((resolve, reject) => {
                    if (abortControllerRef.current.signal.aborted) return reject(new Error('Aborted'));
                    const audioEl = audioRef.current;
                    audioEl.pause();
                    audioEl.src = info.url;
                    audioEl.currentTime = 0;

                    const onEnded = () => { cleanup(); resolve(); };
                    const onError = (e) => { cleanup(); reject(e || new Error('Playback error')); };
                    const onAbort = () => { cleanup(); reject(new Error('Aborted')); };

                    function cleanup() {
                        audioEl.removeEventListener('ended', onEnded);
                        audioEl.removeEventListener('error', onError);
                        abortControllerRef.current.signal.removeEventListener('abort', onAbort);
                        // do NOT clear respTimeoutRef here; let it close the response window itself after responseWindow ms
                    }

                    audioEl.addEventListener('ended', onEnded);
                    audioEl.addEventListener('error', onError);
                    abortControllerRef.current.signal.addEventListener('abort', onAbort);

                    const p = audioEl.play();
                    if (p && typeof p.then === 'function') {
                        p.catch(err => { cleanup(); reject(err); });
                    }
                }).catch(err => {
                    if (err.message === 'Aborted') throw err;
                    console.error('[Test] playback error for', file, err);
                });

                // increment played count
                setTotalAudioPlayed(prev => prev + 1);
                setDisplayScript('');

                // wait the gap before next (if not last)
                if (i < selected.length - 1) {
                    const gap = gaps[i] ?? 0;
                    console.log(`[Test] waiting gap ${gap}s before next stimulus`);
                    await new Promise(resolve => {
                        timeoutRef.current = setTimeout(resolve, gap * 1000);
                    });
                }
            }

            // final gap after last stimulus (await it)
            if (!abortControllerRef.current.signal.aborted) {
                console.log(`[Test] final gap ${finalGapSeconds}s`);
                await new Promise(resolve => {
                    timeoutRef.current = setTimeout(resolve, finalGapSeconds * 1000);
                });
            }

            // cleanup blob URLs
            Object.values(preloadMap).forEach(i => { try { URL.revokeObjectURL(i.url); } catch (e) {} });

            setIsTestRunning(false);
            setShowPopup(true);
            console.log('[Test] run finished normally.');
        } catch (err) {
            if (err && err.message === 'Aborted') {
                console.log('[Test] aborted');
            } else {
                console.error('[Test] fetchSelectedAudio error', err);
                setPopupError(String(err.message || err));
                setShowPopup(true);
            }
            setIsTestRunning(false);
        } finally {
            if (testTimerRef.current) { clearTimeout(testTimerRef.current); testTimerRef.current = null; }
            if (respTimeoutRef.current) {
                clearTimeout(respTimeoutRef.current);
                respTimeoutRef.current = null;
                setIsWithinResponseWindow(false);
            }
        }
    };

    const handleClick = () => {
        if (isWithinResponseWindow) {
            setScore(prev => {
                const next = prev + 1;
                console.log('✅ Click accepted during response window. new score:', next);
                return next;
            });
        } else {
            console.log('⚠️ Click outside response window — ignored');
        }
    };

    const handleExit = () => {
        console.log('[Test] Exit clicked — stopping test');
        setShowPopup(true);
        stopTest();
    };

    const stopTest = () => {
        setIsTestRunning(false);
        try { audioRef.current.pause(); } catch (e) {}
        audioRef.current.src = '';
        clearTimeout(timeoutRef.current);
        if (respTimeoutRef.current) { clearTimeout(respTimeoutRef.current); respTimeoutRef.current = null; }
        clearTimeout(testTimerRef.current);
        try { abortControllerRef.current.abort(); } catch (e) {}
        setCurrentStimulusIndex(0);
        setIsWithinResponseWindow(false);
        setDisplayScript('');
    };

    const handleClosePopup = () => {
        setShowPopup(false);
        setPopupError(null);
        navigate('/home');
    };

    useEffect(() => {
        console.log('[state watch]', { score, totalAudioPlayed, numberOfStimuli, currentStimulusIndex, isWithinResponseWindow, responseWindow });
    }, [score, totalAudioPlayed, numberOfStimuli, currentStimulusIndex, isWithinResponseWindow, responseWindow]);

    return (
        <div className="test-screen11">
            <Goal g={g} sk={sk} />
            <div className="content">
                <h4>{instruction}</h4>
                <div className="audio-icon" onClick={() => { try { audioRef.current.play(); } catch (e) { console.warn(e); } }}>
                    <span role="img" aria-label="speaker">🔊</span>
                </div>
                <h2 className="display-script">{displayScript}</h2>
            </div>

            <div className="button-row-index">
                <button className="click-button" onClick={handleClick} disabled={!isTestRunning}>
                    Click
                </button>
                <button className="exit-button" onClick={handleExit}>Exit</button>
            </div>

            {showPopup && (
                <Popup
                    score={score}
                    totalAudioPlayed={totalAudioPlayed}
                    onClose={handleClosePopup}
                    errorMessage={popupError}
                />
            )}
        </div>
    );
};

export default TestScreen11;

// import React, { useContext, useEffect, useState, useRef } from 'react';
// import DataContext from '../../stores/DataContextProvider';
// import { useNavigate } from 'react-router-dom';
// import backendIP from '../../utils/serverData';
// import Popup from '../popup/Popup';
// import './TestScreen11.css';
// import Goal from '../controllers/Goal';

// const TestScreen11 = () => {
//     const { g, sk, instruction, selectedOptions, folderPath } = useContext(DataContext);
//     const [displayScript, setDisplayScript] = useState('');
//     const [score, setScore] = useState(0);
//     const [totalAudioPlayed, setTotalAudioPlayed] = useState(0);
//     const [showPopup, setShowPopup] = useState(false);
//     const [playedFiles, setPlayedFiles] = useState([]);
//     const [popupError, setPopupError] = useState(null);

//     const audioRef = useRef(new Audio());
//     const abortControllerRef = useRef(new AbortController());
//     const navigate = useNavigate();

//     const [isTestRunning, setIsTestRunning] = useState(false);
//     const [numberOfStimuli, setNumberOfStimuli] = useState(0);
//     const [responseWindow, setResponseWindow] = useState(3000); // ms
//     const [duration, setDuration] = useState(0); // seconds
//     const [currentStimulusIndex, setCurrentStimulusIndex] = useState(0);
//     const [isWithinResponseWindow, setIsWithinResponseWindow] = useState(false);

//     const timeoutRef = useRef(null);           // general purpose timeout
//     const testTimerRef = useRef(null);         // overall test watchdog
//     const startTimeRef = useRef(null);
//     const respTimeoutRef = useRef(null);       // response-window timer (dedicated)

//     const finalGapSeconds = 10; // final gap after last stimulus (seconds)
//     const minGapBetweenStimuli = 10; // minimum gap (seconds) between stimuli required by spec

//     // Parse response window robustly (accepts '4', '4s', '4000', '4000ms', option objects)
//     const parseResponseWindowToMs = (raw) => {
//         if (raw == null) return 3000;
//         if (typeof raw === 'object') raw = raw.value ?? raw.label ?? raw;
//         const s = String(raw).trim().toLowerCase();
//         const m = s.match(/-?\d+(\.\d+)?/);
//         if (!m) return 3000;
//         const num = Number(m[0]);
//         if (Number.isNaN(num)) return 3000;
//         // if <= 10 treat as seconds else treat as ms
//         return Math.abs(num) <= 10 ? Math.round(num * 1000) : Math.round(num);
//     };

//     useEffect(() => {
//         if (selectedOptions && Object.keys(selectedOptions).length > 0) {
//             const durationInMinutes =
//                 parseInt(selectedOptions['Time Duration']?.label ??
//                            selectedOptions['Time Duration']?.value ??
//                            selectedOptions['Time Duration'], 10) || 0;

//             const numStim =
//                 parseInt(selectedOptions['Number of Stimuli']?.label ??
//                           selectedOptions['Number of Stimuli']?.value ??
//                           selectedOptions['Number of Stimuli'], 10) || 0;

//             const rawResp = selectedOptions['Response Window'] ??
//                             selectedOptions['Response Window']?.label ??
//                             selectedOptions['Response Window']?.value;
//             const respMs = parseResponseWindowToMs(rawResp);

//             const totalDurationSec = durationInMinutes * 60;

//             if (!isNaN(numStim) && numStim > 0) {
//                 setNumberOfStimuli(numStim);
//                 setResponseWindow(respMs);
//                 setIsTestRunning(true);
//                 setPlayedFiles([]);
//                 setDuration(totalDurationSec);
//                 setScore(0);
//                 setTotalAudioPlayed(0);
//                 setPopupError(null);
//                 console.log(`[Test] init: durationMin=${durationInMinutes}, stimuli=${numStim}, respMs=${respMs}`);
//             } else {
//                 console.error('Invalid Number of Stimuli', numStim);
//             }
//         }
//     }, [selectedOptions]);

//     useEffect(() => {
//         if (folderPath && numberOfStimuli > 0 && isTestRunning) {
//             console.log('[Test] fetching files from', folderPath);
//             fetchSelectedAudio();
//         }

//         return () => {
//             // full cleanup on unmount or dependency change
//             try { audioRef.current.pause(); } catch (e) {}
//             audioRef.current.src = '';
//             try { abortControllerRef.current.abort(); } catch (e) {}
//             clearTimeout(timeoutRef.current);
//             clearTimeout(testTimerRef.current);
//             if (respTimeoutRef.current) { clearTimeout(respTimeoutRef.current); respTimeoutRef.current = null; }
//         };
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, [folderPath, numberOfStimuli, isTestRunning]);

//     // utility: repeat files to reach desired count and shuffle
//     const makeExactCount = (files, desired) => {
//         const out = [];
//         if (!files || files.length === 0) return out;
//         for (let i = 0; i < desired; i++) out.push(files[i % files.length]);
//         for (let i = out.length - 1; i > 0; i--) {
//             const j = Math.floor(Math.random() * (i + 1));
//             [out[i], out[j]] = [out[j], out[i]];
//         }
//         return out;
//     };

//     const requestFileList = async (folder) => {
//         const resp = await fetch(`${backendIP}/audio/listfiles`, {
//             method: 'POST',
//             headers: { 'Content-Type': 'application/json' },
//             body: JSON.stringify({ folderPath: folder }),
//             signal: abortControllerRef.current.signal,
//         });
//         if (!resp.ok) {
//             const txt = await resp.text().catch(()=>'');
//             throw new Error(`listfiles failed ${resp.status} ${txt}`);
//         }
//         return await resp.json();
//     };

//     const fetchBlobAndMeasure = async (filenameWithPath) => {
//         const resp = await fetch(`${backendIP}/audio/getaudio`, {
//             method: 'POST',
//             headers: { 'Content-Type': 'application/json' },
//             body: JSON.stringify({ filenameWithPath }),
//             signal: abortControllerRef.current.signal,
//         });
//         if (!resp.ok) {
//             const txt = await resp.text().catch(()=>'');
//             throw new Error(`getaudio failed ${resp.status} ${txt}`);
//         }
//         const blob = await resp.blob();
//         const url = URL.createObjectURL(blob);
//         const temp = new Audio();
//         temp.src = url;
//         const dur = await new Promise((resolve, reject) => {
//             const onMeta = () => { cleanup(); resolve(Math.max(0, temp.duration || 0)); };
//             const onErr = () => { cleanup(); reject(new Error('loadedmetadata failed')); };
//             const cleanup = () => { temp.removeEventListener('loadedmetadata', onMeta); temp.removeEventListener('error', onErr); };
//             temp.addEventListener('loadedmetadata', onMeta);
//             temp.addEventListener('error', onErr);
//         });
//         return { url, duration: dur };
//     };

//     // distribute totalAvailable into gapsCount random gaps each >= minGap
//     const generateRandomGaps = (totalAvailable, gapsCount, minGap) => {
//         if (gapsCount <= 0) return [];
//         const minTotal = minGap * gapsCount;
//         if (totalAvailable < minTotal) return null; // not possible
//         const extra = totalAvailable - minTotal;
//         const weights = Array.from({ length: gapsCount }, () => Math.random());
//         const s = weights.reduce((a,b)=>a+b,0) || 1;
//         const gaps = weights.map(w => minGap + (w / s) * extra);
//         return gaps.map(x => Math.round(x * 10) / 10);
//     };

//     const fetchSelectedAudio = async () => {
//         try {
//             // reset/prepare
//             abortControllerRef.current = new AbortController();
//             setPopupError(null);

//             const audioData = await requestFileList(folderPath);
//             if (!Array.isArray(audioData) || audioData.length === 0) {
//                 throw new Error('No audio files returned from server.');
//             }

//             const wavFiles = audioData
//                 .filter(f => typeof f === 'string' && f.trim().toLowerCase().endsWith('.wav'))
//                 .map(f => f.trim());
//             if (wavFiles.length === 0) throw new Error('No .wav files in directory.');

//             // pick exact number of stimuli (repeat if needed)
//             const selected = makeExactCount(wavFiles.sort(() => Math.random() - 0.5), numberOfStimuli);
//             setPlayedFiles(selected);
//             console.log('[Test] selected sequence:', selected);

//             // preload unique files and measure durations
//             const preloadMap = {};
//             const uniques = Array.from(new Set(selected));
//             for (const f of uniques) {
//                 if (abortControllerRef.current.signal.aborted) throw new Error('Aborted');
//                 const path = `${folderPath}/${f}`;
//                 const info = await fetchBlobAndMeasure(path);
//                 preloadMap[f] = info;
//             }

//             // compute sum of durations according to the sequence (count duplicates)
//             let sumAudioDurations = 0;
//             for (const f of selected) sumAudioDurations += (preloadMap[f]?.duration ?? 0);
//             console.log('[Test] sum audio durations (s):', sumAudioDurations);

//             // compute required minimum total: audio durations + (n-1)*minGap + finalGap
//             const n = selected.length;
//             const requiredMin = sumAudioDurations + Math.max(0, (n - 1) * minGapBetweenStimuli) + finalGapSeconds;
//             if (duration < requiredMin) {
//                 const msg = `Not enough total time (${duration}s) to play ${n} stimuli with ${minGapBetweenStimuli}s gaps and ${finalGapSeconds}s final gap. Minimum required ≈ ${Math.ceil(requiredMin)}s`;
//                 console.error(msg);
//                 setPopupError(msg);
//                 setShowPopup(true);
//                 // revoke blobs
//                 Object.values(preloadMap).forEach(i => { try { URL.revokeObjectURL(i.url); } catch (e) {} });
//                 setIsTestRunning(false);
//                 return;
//             }

//             // compute totalAvailableForGaps (exclude final gap)
//             const totalAvailableForGaps = duration - sumAudioDurations - finalGapSeconds;
//             const gapsCount = Math.max(0, n - 1);
//             const gaps = gapsCount > 0
//                 ? generateRandomGaps(totalAvailableForGaps, gapsCount, minGapBetweenStimuli)
//                 : [];
//             if (gapsCount > 0 && !gaps) {
//                 const msg = 'Unable to compute gaps satisfying minimum gap requirement.';
//                 console.error(msg);
//                 setPopupError(msg);
//                 setShowPopup(true);
//                 Object.values(preloadMap).forEach(i => { try { URL.revokeObjectURL(i.url); } catch (e) {} });
//                 setIsTestRunning(false);
//                 return;
//             }
//             console.log('[Test] gaps (s):', gaps, 'finalGap:', finalGapSeconds);

//             // Start playback sequence
//             startTimeRef.current = Date.now();
//             if (testTimerRef.current) clearTimeout(testTimerRef.current);
//             testTimerRef.current = setTimeout(() => {
//                 console.log('[Test watchdog] duration exceeded - aborting');
//                 try { abortControllerRef.current.abort(); } catch (e) {}
//             }, (duration + 5) * 1000); // small margin

//             for (let i = 0; i < selected.length; i++) {
//                 if (abortControllerRef.current.signal.aborted) {
//                     console.log('[Test] aborted during playback at index', i);
//                     break;
//                 }

//                 const file = selected[i];
//                 setCurrentStimulusIndex(i);
//                 setDisplayScript(file.replace('.wav', ''));
//                 console.log(`[Test] playing ${i+1}/${selected.length}: ${file}`);

//                 // Ensure preload info present
//                 const info = preloadMap[file];
//                 if (!info) throw new Error('Missing preload info for ' + file);

//                 // START response window at audio START and schedule closure after responseWindow ms
//                 // Clear any previous respTimeout just in case
//                 if (respTimeoutRef.current) {
//                     clearTimeout(respTimeoutRef.current);
//                     respTimeoutRef.current = null;
//                 }
//                 setIsWithinResponseWindow(true);
//                 respTimeoutRef.current = setTimeout(() => {
//                     respTimeoutRef.current = null;
//                     setIsWithinResponseWindow(false);
//                     console.log('[Test] response window closed after configured ms');
//                 }, responseWindow);

//                 // Play audio and await its ended event (but do NOT clear respTimeoutRef on ended)
//                 await new Promise((resolve, reject) => {
//                     if (abortControllerRef.current.signal.aborted) return reject(new Error('Aborted'));
//                     const audioEl = audioRef.current;
//                     audioEl.pause();
//                     audioEl.src = info.url;
//                     audioEl.currentTime = 0;

//                     const onEnded = () => { cleanup(); resolve(); };
//                     const onError = (e) => { cleanup(); reject(e || new Error('Playback error')); };
//                     const onAbort = () => { cleanup(); reject(new Error('Aborted')); };

//                     function cleanup() {
//                         audioEl.removeEventListener('ended', onEnded);
//                         audioEl.removeEventListener('error', onError);
//                         abortControllerRef.current.signal.removeEventListener('abort', onAbort);
//                         // IMPORTANT: do NOT clear respTimeoutRef here — we must let the response window remain until its timeout.
//                     }

//                     audioEl.addEventListener('ended', onEnded);
//                     audioEl.addEventListener('error', onError);
//                     abortControllerRef.current.signal.addEventListener('abort', onAbort);

//                     const p = audioEl.play();
//                     if (p && typeof p.then === 'function') {
//                         p.catch(err => { cleanup(); reject(err); });
//                     }
//                 }).catch(err => {
//                     if (err.message === 'Aborted') throw err;
//                     console.error('[Test] playback error for', file, err);
//                 });

//                 // increment played count
//                 setTotalAudioPlayed(prev => prev + 1);
//                 setDisplayScript('');

//                 // wait the gap before next (if not last)
//                 if (i < selected.length - 1) {
//                     const gap = gaps[i] ?? 0;
//                     console.log(`[Test] waiting gap ${gap}s before next stimulus`);
//                     await new Promise(resolve => {
//                         timeoutRef.current = setTimeout(resolve, gap * 1000);
//                     });
//                 }
//             }

//             // final gap after last stimulus (await it)
//             if (!abortControllerRef.current.signal.aborted) {
//                 console.log(`[Test] final gap ${finalGapSeconds}s`);
//                 await new Promise(resolve => {
//                     timeoutRef.current = setTimeout(resolve, finalGapSeconds * 1000);
//                 });
//             }

//             // cleanup blob URLs
//             Object.values(preloadMap).forEach(i => { try { URL.revokeObjectURL(i.url); } catch (e) {} });

//             setIsTestRunning(false);
//             setShowPopup(true);
//             console.log('[Test] run finished normally.');
//         } catch (err) {
//             if (err && err.message === 'Aborted') {
//                 console.log('[Test] aborted');
//             } else {
//                 console.error('[Test] fetchSelectedAudio error', err);
//                 setPopupError(String(err.message || err));
//                 setShowPopup(true);
//             }
//             setIsTestRunning(false);
//         } finally {
//             if (testTimerRef.current) { clearTimeout(testTimerRef.current); testTimerRef.current = null; }
//             // clear response timeout if test fully finished or aborted
//             if (respTimeoutRef.current) {
//                 clearTimeout(respTimeoutRef.current);
//                 respTimeoutRef.current = null;
//                 setIsWithinResponseWindow(false);
//             }
//         }
//     };

//     const handleClick = () => {
//         if (isWithinResponseWindow) {
//             setScore(prev => {
//                 const next = prev + 1;
//                 console.log('✅ Click accepted during response window. new score:', next);
//                 return next;
//             });
//         } else {
//             console.log('⚠️ Click outside response window — ignored');
//         }
//     };

//     const handleExit = () => {
//         console.log('[Test] Exit clicked — stopping test');
//         setShowPopup(true);
//         stopTest();
//     };

//     const stopTest = () => {
//         setIsTestRunning(false);
//         try { audioRef.current.pause(); } catch (e) {}
//         audioRef.current.src = '';
//         clearTimeout(timeoutRef.current);
//         if (respTimeoutRef.current) { clearTimeout(respTimeoutRef.current); respTimeoutRef.current = null; }
//         clearTimeout(testTimerRef.current);
//         try { abortControllerRef.current.abort(); } catch (e) {}
//         setCurrentStimulusIndex(0);
//         setIsWithinResponseWindow(false);
//         setDisplayScript('');
//     };

//     const handleClosePopup = () => {
//         setShowPopup(false);
//         setPopupError(null);
//         navigate('/home');
//     };

//     useEffect(() => {
//         console.log('[state watch]', { score, totalAudioPlayed, numberOfStimuli, currentStimulusIndex, isWithinResponseWindow, responseWindow });
//     }, [score, totalAudioPlayed, numberOfStimuli, currentStimulusIndex, isWithinResponseWindow, responseWindow]);

//     return (
//         <div className="test-screen11">
//             <Goal g={g} sk={sk} />
//             <div className="content">
//                 <h4>{instruction}</h4>
//                 <div className="audio-icon" onClick={() => { try { audioRef.current.play(); } catch (e) { console.warn(e); } }}>
//                     <span role="img" aria-label="speaker">🔊</span>
//                 </div>
//                 <h2 className="display-script">{displayScript}</h2>
//             </div>

//             <div className="button-row-index">
//                 <button className="click-button" onClick={handleClick} disabled={!isTestRunning}>
//                     Click
//                 </button>
//                 <button className="exit-button" onClick={handleExit}>Exit</button>
//             </div>

//             {showPopup && (
//                 <Popup
//                     score={score}
//                     totalAudioPlayed={totalAudioPlayed}
//                     onClose={handleClosePopup}
//                     errorMessage={popupError}
//                 />
//             )}
//         </div>
//     );
// };

// export default TestScreen11;
